import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Prospect } from '@/types/prospect';
import { useOptimizedCampaignData } from '@/contexts/OptimizedCampaignDataContext';
import { useProspects } from '@/hooks/useProspects';
import { useToast } from '@/hooks/use-toast';

interface ProspectFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
  editingProspect?: Prospect | null;
}

export function ProspectForm({
  open,
  onOpenChange,
  listId,
  editingProspect,
}: ProspectFormProps) {
  const { addProspectsToList, updateProspectInList } =
    useOptimizedCampaignData();
  const { createProspect, validatePhone, formatPhone } = useProspects();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    company: '',
    email: '',
    notes: '',
  });

  useEffect(() => {
    if (editingProspect) {
      setFormData({
        name: editingProspect.name,
        phone: editingProspect.phone,
        company: editingProspect.company,
        email: editingProspect.email || '',
        notes: editingProspect.notes || '',
      });
    } else {
      setFormData({
        name: '',
        phone: '',
        company: '',
        email: '',
        notes: '',
      });
    }
  }, [editingProspect, open]);

  const handleSave = () => {
    // Validation
    if (!formData.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a name.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.phone.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a phone number.',
        variant: 'destructive',
      });
      return;
    }

    if (!validatePhone(formData.phone)) {
      toast({
        title: 'Invalid Phone Number',
        description: 'US/Canada: enter 10 digits (e.g. 5551234567). International: include + and country code (e.g. +50769285069).',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.company.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a company name.',
        variant: 'destructive',
      });
      return;
    }

    const prospectData = {
      name: formData.name.trim(),
      phone: formatPhone(formData.phone.trim()),
      company: formData.company.trim(),
      email: formData.email.trim() || undefined,
      notes: formData.notes.trim() || undefined,
    };

    if (editingProspect) {
      updateProspectInList(listId, editingProspect.id, prospectData);
      toast({
        title: 'Prospect Updated',
        description: 'Prospect has been updated successfully.',
      });
    } else {
      const newProspect = createProspect(prospectData);
      addProspectsToList(listId, [newProspect]);
      toast({
        title: 'Prospect Added',
        description: 'Prospect has been added to the list.',
      });
    }

    onOpenChange(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      company: '',
      email: '',
      notes: '',
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-md'>
        <DialogHeader>
          <DialogTitle>
            {editingProspect ? 'Edit Prospect' : 'Add New Prospect'}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <div>
            <Label htmlFor='prospect-name'>Name *</Label>
            <Input
              id='prospect-name'
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder='e.g., John Smith'
            />
          </div>

          <div>
            <Label htmlFor='prospect-phone'>Phone *</Label>
            <Input
              id='prospect-phone'
              value={formData.phone}
              onChange={(e) => {
                const value = e.target.value;
                // Allow digits, +, -, (, ), and spaces only
                if (/^[+]?[0-9\-() ]*$/.test(value)) {
                  setFormData((prev) => ({ ...prev, phone: value }));
                }
              }}
              placeholder='e.g., (555) 123-4567 or +50769285069'
              type='tel'
            />
            <p className='text-xs text-muted-foreground mt-1'>
              US/Canada: enter normally. Other countries: include + and country code, no spaces (e.g. +50769285069)
            </p>
          </div>

          <div>
            <Label htmlFor='prospect-company'>Company *</Label>
            <Input
              id='prospect-company'
              value={formData.company}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, company: e.target.value }))
              }
              placeholder='e.g., ABC HVAC Services'
            />
          </div>

          <div>
            <Label htmlFor='prospect-email'>Email</Label>
            <Input
              id='prospect-email'
              type='email'
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder='e.g., john@abchvac.com'
            />
          </div>

          <div>
            <Label htmlFor='prospect-notes'>Notes</Label>
            <Textarea
              id='prospect-notes'
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder='Additional notes about this prospect...'
              rows={3}
            />
          </div>

          <div className='flex gap-2 pt-4'>
            <Button
              variant='outline'
              onClick={() => handleOpenChange(false)}
              className='flex-1'
            >
              Cancel
            </Button>
            <Button onClick={handleSave} className='flex-1'>
              {editingProspect ? 'Update Prospect' : 'Add Prospect'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
